import { useEffect, useRef, useState } from 'react'

export const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3]
const FIT_PADDING_PX = 60

export function nextZoom(current, direction) {
  const idx = ZOOM_STEPS.findIndex(z => Math.abs(z - current) < 0.01)
  if (idx === -1)
    return direction > 0 ? (ZOOM_STEPS.find(z => z > current) ?? current) : ([...ZOOM_STEPS].reverse().find(z => z < current) ?? current)
  return ZOOM_STEPS[Math.min(ZOOM_STEPS.length - 1, Math.max(0, idx + direction))]
}

// Manage fixed and fit-to-width PDF zoom levels.
export default function usePageZoom({ pageWidthPoints = 612, pdfLoading = false } = {}) {
  const [zoom, setZoom] = useState('fit')
  const [fitWidth, setFitWidth] = useState(900)
  const scrollRef = useRef(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return undefined
    const measure = () => setFitWidth(Math.max(320, el.clientWidth - FIT_PADDING_PX))
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [pdfLoading])

  const scale = zoom === 'fit' ? fitWidth / pageWidthPoints : zoom
  const zoomPercent = Math.round(scale * 100)

  return {
    zoom,
    scale,
    zoomPercent,
    scrollRef,
    isFit: zoom === 'fit',
    canZoomOut: zoomPercent > ZOOM_STEPS[0] * 100,
    canZoomIn: zoomPercent < ZOOM_STEPS[ZOOM_STEPS.length - 1] * 100,
    zoomIn: () => setZoom(nextZoom(scale, 1)),
    zoomOut: () => setZoom(nextZoom(scale, -1)),
    fitToWidth: () => setZoom('fit')
  }
}
